import { alertInGrain } from "../utils/grainDialogs.js";
import { useRef, useState } from "react";
import { useOptimizationJobTracker } from "./useOptimizationJobTracker.js";
import { canRequestOptimizationCancellation } from "./useOptimizationJobTracker.js";
import { clearAppStatusNotice, dispatchAppStatusNotice } from "../utils/appStatusNotice.js";
import { dispatchResultsShellNavigation } from "../utils/resultsDetailViewNavigation.js";
import {
  launchOptimizationViaApi,
  resolveOptimizationLaunchPreflight,
} from "../utils/optimizationLaunch.js";
import { buildStatusNoticeAlertCaptureContext } from "../lib/dispatchAlertCaptureContext.js";

function getOptimizationLaunchErrorMessage(error) {
  return error instanceof Error && error.message
    ? error.message
    : "Le lancement de l'optimisation a échoué.";
}

function measureOptionalDurationMs(startedAt) {
  if (!Number.isFinite(startedAt) || typeof performance === "undefined" || typeof performance.now !== "function") {
    return null;
  }

  const elapsedMs = performance.now() - startedAt;
  return Number.isFinite(elapsedMs) && elapsedMs >= 0
    ? Math.round(elapsedMs * 10) / 10
    : null;
}

export function publishOptimizationPreflightFailure(issue, {
  dispatchNavigation = dispatchResultsShellNavigation,
  dispatchNotice = dispatchAppStatusNotice,
  operationId = "",
} = {}) {
  if (!issue || typeof issue !== "object") {
    return false;
  }

  const captureContext = buildStatusNoticeAlertCaptureContext({
    action: "optimization",
    actionLabel: "Optimiser",
    captureContext: {
      action: "optimization",
      action_label: "Optimiser",
      interaction_origin: "direct",
      operation_id: operationId,
      source: "optimization",
      technical: {
        correlation_id: operationId,
        failed_stage: "preflight",
        failure_kind: "validation",
        reason_code: issue.filterKey || issue.code || "optimization_preflight",
      },
    },
    detailItems: Array.isArray(issue.detailItems) ? issue.detailItems : [],
    operationId,
    source: "optimization",
    status: "failed",
    title: issue.title || "Optimisation bloquée",
  });

  dispatchNotice({
    action: "optimization",
    actionLabel: "Optimiser",
    captureContext,
    detailItems: Array.isArray(issue.detailItems) ? issue.detailItems : [],
    eyebrow: "Optimisation de la flotte",
    message: issue.message || "L'optimisation ne peut pas être lancée dans l'état actuel.",
    source: "optimization",
    status: "failed",
    statusLabel: "Action requise",
    title: issue.title || "Optimisation bloquée",
  });

  if (issue.filterKey === "technical_precheck") {
    dispatchNavigation({
      errorId: "",
      errorsOpenAll: true,
      filter: "technical_precheck",
      scroll: true,
      source: "",
      syncLocation: true,
      viewName: "errors",
    });
  }

  return true;
}

export default function useOptimizationLaunchAction() {
  const isLaunchingRef = useRef(false);
  const [isLaunching, setIsLaunching] = useState(false);
  const { jobState, trackOptimizationLaunch } = useOptimizationJobTracker();
  const { requestOptimizationCancel } = useOptimizationJobTracker();
  const isOptimizing = isLaunching || ["running", "cancelling"].includes(jobState.status);
  const isCancelling = jobState.status === "cancelling";
  const isFinalizing = jobState.status === "running"
    && jobState.cancellationAllowed === false
    && jobState.operationKind !== "planning_reset";
  const canCancelOptimization = canRequestOptimizationCancellation(jobState);

  const launchOptimization = ({
    dispatchDate = undefined,
    dispatchPeriod = undefined,
    form = null,
    includeSuspendedDeliveries = false,
    optimizationMode = null,
    preflight = null,
    schedule = null,
  } = {}) => {
    if (!(form instanceof HTMLFormElement)) {
      return false;
    }

    const runtimeIsOptimizing = isLaunchingRef.current || isLaunching || ["running", "cancelling"].includes(jobState.status);

    const confirmClickAtEpochMs = Date.now();
    const confirmClickAt = new Date(confirmClickAtEpochMs).toISOString();
    const launchOperationId = `optimization:${confirmClickAt}`;
    const preflightStartedAt = typeof performance !== "undefined" && typeof performance.now === "function"
      ? performance.now()
      : null;

    const preflightResult = resolveOptimizationLaunchPreflight({
      ...(preflight && typeof preflight === "object" ? preflight : {}),
      includeSuspendedDeliveries,
      isOptimizing: runtimeIsOptimizing,
    });

    const preflightMs = measureOptionalDurationMs(preflightStartedAt);

    if (!preflightResult.ok) {
      publishOptimizationPreflightFailure(preflightResult.issue, { operationId: launchOperationId });
      return false;
    }

    clearAppStatusNotice();
    isLaunchingRef.current = true;
    setIsLaunching(true);

    const runLaunch = async () => {
      try {
        const launchState = await launchOptimizationViaApi({
          dispatchDate,
          dispatchPeriod,
          includeSuspendedDeliveries,
          optimizationMode,
        });
        trackOptimizationLaunch({
          ...launchState,
          referenceProbe: {
            acceptedAt: launchState?.timing?.acceptedAt || null,
            acceptedAtEpochMs: launchState?.timing?.acceptedAtEpochMs ?? null,
            confirmClickAt,
            confirmClickAtEpochMs,
            postOptimizeRoundtripMs: launchState?.timing?.postOptimizeRoundtripMs ?? null,
            preflightMs,
          },
        });
      } catch (error) {
        const errorMessage = getOptimizationLaunchErrorMessage(error);
        const captureContext = buildStatusNoticeAlertCaptureContext({
          action: "optimization",
          actionLabel: "Optimiser",
          captureContext: {
            action: "optimization",
            action_label: "Optimiser",
            interaction_origin: "direct",
            operation_id: launchOperationId,
            source: "optimization",
            technical: {
              correlation_id: error?.correlationId || launchOperationId,
              failed_stage: error?.failedStage || "",
              failure_kind: Number.isInteger(Number(error?.status)) ? "http" : "network",
              http_status: error?.status,
              method: "POST",
              path: error?.requestPath || "/api/optimize",
              reason_code: error?.reasonCode || "",
            },
          },
          operationId: launchOperationId,
          status: "failed",
          title: "Optimisation de la flotte interrompue",
        });

        trackOptimizationLaunch({
          captureContext,
          error: errorMessage,
          finishedAt: new Date().toISOString(),
          message: errorMessage,
          referenceProbe: {
            confirmClickAt,
            confirmClickAtEpochMs,
            preflightMs,
          },
          status: "failed",
        });

        await alertInGrain(errorMessage, { title: "Optimisation de la flotte interrompue" });
      } finally {
        isLaunchingRef.current = false;
        setIsLaunching(false);
      }
    };

    if (typeof schedule === "function") {
      schedule(runLaunch);
      return true;
    }

    void runLaunch();
    return true;
  };

  return {
    canCancelOptimization,
    isCancelling,
    isFinalizing,
    isOptimizing,
    launchOptimization,
    requestOptimizationCancel,
  };
}
